import React, { FC, useState, useEffect } from 'react';
import SalesArea from 'components/SalesArea/SalesArea';
import AddressForm from 'components/SalesArea/pages/AddressForm/Container/AddressForm';
import Cart from 'components/SalesArea/pages/Cart/Cart';
import ProductsList from 'components/SalesArea/pages/ProductList/ProductsList';
import {
  useCheckoutGetQuery,
  useCheckoutCreateMutation,
  CheckoutFragmentFragment,
  useCollectionGetQuery,
  CollectionFragmentFragment,
  useCheckoutLineItemsRemoveMutation,
} from 'graphql/shopify-store-front-api/queries/types.d';

/**
 *  SalesArea表示内容
 * products:        販売商品を表示
 * cart:            カート情報を表示
 * addressForm:     配送先・請求先情報入力フォームを表示
 */
export type PageStatesType = 'products' | 'cart' | 'addressForm';

/**
 * 商品販売に関する一連のフォームを表示するコンポーネント
 * pageStateの値により表示する内容を切り替える
 */
const EnhancedLiveSalesArea: FC = () => {
  // コレクション情報を取得するhooks
  const {
    data: collectionGetData,
    loading: isCollectionGetLoading,
  } = useCollectionGetQuery({
    variables: { id: process.env.REACT_APP_SHOPIFY_GET_COLLECTION_ID ?? '' },
    context: { clientName: 'shopify' },
  });

  const collection = isCollectionGetLoading
    ? undefined
    : (collectionGetData?.node as CollectionFragmentFragment);

  // チェックアウトIDを発行するhooks
  const [
    checkoutCreateMutation,
    { loading: isCheckoutCreateLoading },
  ] = useCheckoutCreateMutation({
    variables: { input: {} },
  });

  // LineItemを削除するhooks
  const [
    checkoutLineItemsRemoveMutation,
    { loading: checkoutLineItemsRemoveLoading },
  ] = useCheckoutLineItemsRemoveMutation();

  // チェックアウト情報を取得するhooks
  // 支払い処理終了後カートをリフレッシュする必要があるため、3000ms毎にキャッシュを更新する
  const {
    data: checkoutGetData,
    loading: isCheckoutGetLoading,
  } = useCheckoutGetQuery({
    variables: { id: localStorage.getItem('checkoutId') ?? '' },
    pollInterval: 3000,
    context: { clientName: 'shopify' },
  });

  const checkout = isCheckoutGetLoading
    ? undefined
    : (checkoutGetData?.node as CheckoutFragmentFragment);

  // 現在の表示内容を保持するState
  // Auth0ログイン画面からのコールバック時、初期表示をカート画面にする
  const [pageState, setPageState] = useState<PageStatesType>(
    localStorage.getItem('auth_show') ? 'cart' : 'products',
  );
  // LocalStorageにcheckoutIdが設定されたか否かを保持するState
  const [isLocalStorageSetting, setIsLocalStorageSetting] = useState(false);

  // LocalStorageにcheckoutIdが設定されていない、
  // もしくは設定されているcheckoutIdがコンプリート済みの状態だった場合checkoutIdを発行
  useEffect(() => {
    if (
      !localStorage.getItem('checkoutId') ||
      checkout?.completedAt ||
      (!isCheckoutGetLoading && !checkout)
    ) {
      void localStorage.removeItem('checkoutId');
      void checkoutCreateMutation().then((response) => {
        if (response.data?.checkoutCreate?.checkout?.id) {
          localStorage.setItem(
            'checkoutId',
            response.data?.checkoutCreate?.checkout?.id,
          );
        }
        // locasStorageの設定には少し時間がかかるため0.5s待機させる
        setTimeout(() => {
          setIsLocalStorageSetting(true);
        }, 500);
      });
    } else {
      setIsLocalStorageSetting(true);
    }
  }, [checkout, checkoutCreateMutation, isCheckoutGetLoading]);

  // カートの中にコレクションから情報が取得できない商品（アーカイブ済みや下書きに変更した商品）があった場合、
  // カートから削除する
  useEffect(() => {
    if (checkout && collection) {
      checkout.lineItems.edges.forEach((lineItem) => {
        if (
          !collection.products.edges.find((product) =>
            product.node.variants.edges.find(
              (variant) => variant.node.id === lineItem.node.variant?.id,
            ),
          )
        ) {
          void checkoutLineItemsRemoveMutation({
            variables: {
              checkoutId: localStorage.getItem('checkoutId') ?? '',
              lineItemIds: [lineItem.node.id],
            },
          });
        }
      });
    }
  }, [checkout, checkoutLineItemsRemoveMutation, collection]);

  const salesArea = () => {
    if (pageState === 'products') {
      return (
        <ProductsList
          collection={collection}
          isLoading={isCollectionGetLoading}
          pagingFunction={() => setPageState('cart')}
        />
      );
    }
    if (pageState === 'cart') {
      return (
        <Cart
          nextPagingFunction={() => setPageState('addressForm')}
          previousPagingFunction={() => setPageState('products')}
          checkout={
            isCheckoutGetLoading
              ? undefined
              : (checkoutGetData?.node as CheckoutFragmentFragment)
          }
          isLoading={isCheckoutGetLoading}
        />
      );
    }
    if (pageState === 'addressForm') {
      return (
        <AddressForm
          pagingFunction={() => setPageState('products')}
          checkoutURL={
            isCheckoutGetLoading
              ? ''
              : ((checkoutGetData?.node as CheckoutFragmentFragment)
                  .webUrl as string)
          }
        />
      );
    }

    return <></>;
  };

  return (
    <>
      {(collection?.products.edges.length ?? 0) > 0 && checkout && (
        <SalesArea
          isLoading={
            isCheckoutGetLoading ||
            isCollectionGetLoading ||
            isCheckoutCreateLoading ||
            checkoutLineItemsRemoveLoading ||
            !isLocalStorageSetting
          }
          itemCount={
            checkoutGetData
              ? (checkoutGetData?.node as CheckoutFragmentFragment).lineItems
                  .edges.length
              : 0
          }
          productButtonOnClickFunction={() => setPageState('products')}
          cartButtonOnClickFunction={() => setPageState('cart')}
        >
          {salesArea()}
        </SalesArea>
      )}
    </>
  );
};

export default EnhancedLiveSalesArea;
